<template>
  <VList
    :density="props.density"
    :data-cy="props.dataCy"
    :width="props.width"
    :min-width="props.minWidth"
    :max-width="props.maxWidth"
  >
    <template v-if="props.loading">
      <div class="px-ms2">
        <EasySkeletonLoader
          structure="list-item"
          data-cy="loader__easy-list"
        />
      </div>
    </template>

    <template v-else-if="props.items?.length">
      <EasyDivider
        v-if="withDivider && props.items.length > 0"
        class="m-0"
      />

      <template
        v-for="(item, index) in props.items"
        :key="item.name"
      >
        <VListItem
          v-easy-tooltip="{ text: item.tooltip, disabled: !item.tooltip }"
          :target="props.openInNewTab ? '_blank' : undefined"
          :href="item.path"
          :min-height="props.minHeight"
          :disabled="item.disabled"
          :data-cy="item.dataCy"
          :class="{ 'bg-highlight': item.active, [item.class || '']: !!item.class }"
          @click.exact="onClick(item)"
          @click.ctrl.exact="onClickNewTab(item)"
          @click.meta.exact="onClickNewTab(item)"
          @click.middle.exact="onClickNewTab(item)"
        >
          <template
            v-if="item.icon"
            #prepend
          >
            <DSIcon
              :icon="item.icon as DSIconName"
              class="mr-2"
            />
          </template>

          <VListItemTitle>
            {{ item.name }}
          </VListItemTitle>

          <VListItemSubtitle v-if="item.description">
            {{ item.description }}
          </VListItemSubtitle>

          <template
            v-if="slots.append"
            #append
          >
            <slot
              name="append"
              :item="item"
            />
          </template>
        </VListItem>

        <EasyDivider
          v-if="withDivider && index < props.items.length"
          class="m-0"
        />
      </template>
    </template>
  </VList>
</template>

<script setup lang="ts" generic="T = unknown">
import { useSlots } from "vue";
import { VuetifyDensity } from "@/src/easy-ui/types";
import { DSIcon } from "@/src/design_system";
import type { DSIconName } from "@/src/design_system/primitives/icon/types/iconName.generated";
import { ListItem } from "@/src/easy-ui/types/easyList";
import EasySkeletonLoader from "@/src/easy-ui/components/EasySkeletonLoader.vue";
import EasyDivider from "@/src/easy-ui/components/EasyDivider.vue";
import { VueSlots } from "@/src/shared/types/slot";

interface Props {
  items: ListItem<T>[];
  dataCy?: string;
  minHeight?: string;
  density?: VuetifyDensity;
  loading?: boolean;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  withDivider?: boolean;
  openInNewTab?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  dataCy: undefined,
  minHeight: undefined,
  density: VuetifyDensity.Default,
  loading: false,
  width: undefined,
  minWidth: undefined,
  maxWidth: undefined,
  withDivider: false,
  openInNewTab: false,
});

const emit = defineEmits<{
  (e: "click:menu-item", value: ListItem<T>): void;
  (e: "click:menu-item-new-tab", value: ListItem): void;
}>();

const onClick = ({ handler, ...itemData }: ListItem<T>) => {
  handler?.();
  emit("click:menu-item", itemData);
};

const slots = useSlots() as VueSlots<["append"]>;

const onClickNewTab = ({ handler, ...itemData }: ListItem) => {
  handler?.();
  emit("click:menu-item-new-tab", itemData);
};
</script>
